/* History helpers — recent runs kept on-device (localStorage, capped).
 *
 * Plain functions over the persisted array; the App owns the state + LS
 * persistence. localStorage (not IndexedDB) keeps v1 simple — entries are small
 * and the cap bounds total size well under the quota. */

import { matchSnippet } from '../../data/snippets';
import type { HistoryEntry } from '../../app-types';

/** Most-recent runs to retain (bounds localStorage footprint). */
export const HISTORY_CAP = 20;

/** Short, human title for a run: matched example name, else first real line. */
export function entryTitle(code: string): string {
  const snip = matchSnippet(code);
  if (snip) return snip.title;
  const line = code
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l && l !== '<?php' && !l.startsWith('//') && !l.startsWith('#'));
  if (!line) return 'Đoạn mã không tên';
  return line.length > 42 ? line.slice(0, 42) + '…' : line;
}

/** Local HH:MM label for the run time (on-device, no relative-time ticking). */
export function timeLabel(now: Date = new Date()): string {
  return now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

interface NewRun {
  code: string;
  versions: string[];
  stdin?: string;
  args?: string;
}

/** Stable signature of a run for dedupe (code + inputs + version set). */
function runSig(r: { code: string; versions: string[]; stdin?: string; args?: string }): string {
  return [r.code, r.stdin || '', r.args || '', r.versions.join(',')].join(' ');
}

/** Prepend a run to history, dedupe an immediately-repeated identical run, cap. */
export function pushRun(list: HistoryEntry[], run: NewRun): HistoryEntry[] {
  // Dedupe: skip only exact repeat (same code + inputs + versions).
  if (list[0] && runSig(list[0]) === runSig(run)) return list;
  const entry: HistoryEntry = {
    id: Date.now(),
    title: entryTitle(run.code),
    code: run.code,
    versions: run.versions,
    when: timeLabel(),
    stdin: run.stdin || undefined,
    args: run.args || undefined,
  };
  return [entry, ...list].slice(0, HISTORY_CAP);
}
